import { useMemo } from 'react';

import { Skeleton, Table, TableBody, TableCell, TableHeader, TableRow, Text } from '@/_components';
import { CellName, CurrencyWithPrivacy, TableHeading } from '@/_containers';
import { formatPrimaryDomain, PriceFrequencyAbbrev, TruncatedTextWithTooltip } from '@/_pages/invoices/shared';
import type { InvoiceAgreementDeliverableQuerySchema, InvoiceQuerySchema } from '@/_pages/invoices/shared/schemas';

type InvoiceDeliverablesPanelProps = {
  hasSelection: boolean;
  selectedInvoice: InvoiceQuerySchema | null;
  isLoading: boolean;
  isError: boolean;
};

type InvoiceDeliverableRow = {
  /** Stable unique key per table row (agreement id alone duplicates when one agreement has many line items). */
  rowKey: string;
  /** Agreement primary id for `/agreements/$id` navigation. */
  id: string;
  agreementId: string;
  sku: string;
  lineItem: string;
  domain: string;
  priceFrequency: InvoiceAgreementDeliverableQuerySchema['price_frequency'];
  price: number;
  quantity: number;
};

const compactCellClassName = 'px-2 py-1.5';
const compactHeaderHeadClassName = '!h-auto min-h-0 py-2 px-1.5 first:!pl-2 last:!pr-2 whitespace-nowrap';
const compactHeadingTextClassName = '';

export function InvoiceDeliverablesPanel({
  hasSelection,
  selectedInvoice,
  isLoading,
  isError,
}: InvoiceDeliverablesPanelProps) {
  const deliverableRows = useMemo<InvoiceDeliverableRow[]>(() => {
    const agreementLinksSource = selectedInvoice?.agreement_links ?? [];
    if (!agreementLinksSource.length) {
      return [];
    }

    return agreementLinksSource.flatMap((agreementLink) => {
      if (!agreementLink.invoice_deliverables?.length) {
        return [];
      }

      return agreementLink.invoice_deliverables.map((deliverable) => ({
        rowKey: `${agreementLink.id}-${deliverable.id}`,
        id: agreementLink.agreement?.id != null ? String(agreementLink.agreement.id) : '',
        agreementId:
          agreementLink.agreement?.agreement_id?.trim() ||
          (agreementLink.agreement?.id != null ? String(agreementLink.agreement.id) : '—'),
        sku: deliverable.sku?.trim() ? deliverable.sku : '—',
        lineItem: deliverable.product_name ?? '—',
        domain: formatPrimaryDomain(agreementLink.agreement?.primary_domains),
        priceFrequency: deliverable.price_frequency,
        price: deliverable.price ?? 0,
        quantity: deliverable.quantity ?? 0,
      }));
    });
  }, [selectedInvoice]);

  if (!hasSelection) {
    return (
      <div className="border-c-border text-c-card-muted flex h-full items-center justify-center rounded-t-none rounded-b-xl border px-2 text-sm">
        Select an invoice row to view line items.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="border-c-border flex h-full flex-col rounded-t-none rounded-b-xl border px-2 py-2">
        <div className="space-y-2">
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !selectedInvoice) {
    return (
      <div className="border-c-border text-c-card-muted flex h-full items-center justify-center rounded-t-none rounded-b-xl border px-2 text-sm">
        Failed to load line items.
      </div>
    );
  }

  return (
    <div className="border-c-border flex h-full flex-col rounded-t-none rounded-b-xl border">
      <Table allowScroll={false} containerClassName="min-h-0 flex-1 overflow-auto">
        <TableHeader flatRoundedCorners className="sticky top-0 z-10 border-b bg-gray-50 dark:bg-gray-800">
          <TableRow className="border-b-0 hover:bg-transparent">
            <TableHeading
              title="Agreement #"
              headerClassName={compactHeaderHeadClassName}
              className={compactHeadingTextClassName}
            />
            <TableHeading
              title="SKU #"
              headerClassName={compactHeaderHeadClassName}
              className={compactHeadingTextClassName}
            />
            <TableHeading
              title="Line Item"
              headerClassName={`${compactHeaderHeadClassName} min-w-56`}
              className={compactHeadingTextClassName}
            />
            <TableHeading
              title="Domain"
              headerClassName={`${compactHeaderHeadClassName} min-w-24`}
              className={compactHeadingTextClassName}
            />
            <TableHeading
              title="Cycle"
              headerClassName={`${compactHeaderHeadClassName} text-left`}
              className={compactHeadingTextClassName}
            />
            <TableHeading
              title="Quantity"
              align="right"
              headerClassName={`${compactHeaderHeadClassName} text-right`}
              className={compactHeadingTextClassName}
            />
            <TableHeading
              title="Price"
              align="right"
              headerClassName={`${compactHeaderHeadClassName} text-right`}
              className={compactHeadingTextClassName}
            />
            <TableHeading
              title="Total"
              align="right"
              headerClassName={`${compactHeaderHeadClassName} text-right`}
              className={compactHeadingTextClassName}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {deliverableRows.length > 0 ? (
            deliverableRows.map((row) => (
              <TableRow key={row.rowKey}>
                <TableCell className={compactCellClassName}>
                  <CellName
                    name={row.agreementId}
                    showAvatar={false}
                    to="/agreements/$id"
                    params={{ id: row.id }}
                    state={{ naturalBack: true }}
                    target="_blank"
                    className="text-c-card-muted text-xs"
                  />
                </TableCell>
                <TableCell className={compactCellClassName}>
                  <Text className="text-c-card-muted text-xs">{row.sku}</Text>
                </TableCell>
                <TableCell className={`min-w-56 ${compactCellClassName}`}>
                  <TruncatedTextWithTooltip text={row.lineItem} className="text-c-card-muted text-xs" />
                </TableCell>
                <TableCell className={`min-w-24 ${compactCellClassName}`}>
                  <Text className="text-c-card-muted text-xs">{row.domain}</Text>
                </TableCell>
                <TableCell className={`text-left ${compactCellClassName}`}>
                  <PriceFrequencyAbbrev priceFrequency={row.priceFrequency} className="text-c-card-muted text-xs" />
                </TableCell>
                <TableCell className={`text-right ${compactCellClassName}`}>
                  <Text className="text-c-card-muted text-xs">{row.quantity}</Text>
                </TableCell>
                <TableCell className={`text-right ${compactCellClassName}`}>
                  <Text className="text-c-card-muted text-xs">
                    <CurrencyWithPrivacy value={row.price} />
                  </Text>
                </TableCell>
                <TableCell className={`text-right ${compactCellClassName}`}>
                  <Text className="text-c-card-muted text-xs">
                    <CurrencyWithPrivacy value={row.price * row.quantity} />
                  </Text>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} className="text-c-card-muted py-6 text-center text-xs">
                No line items available for this invoice.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
